(function () {
  "use strict";

  a3_cabin_reports.controller("LotteryCtrl", LotteryCtrl);

  /**
   * @memberof CabinReports
   * @ngdoc controller
   * @name LotteryCtrl
   * @description
   *   Shows the details of a single Lottery for a given flight.
   *   Handles lottery execution, winner/substitute selection, contact capture,
   *   draft persistence and final submission.
   * @ngInject
   */
  // Main controller for the Lottery feature. Handles all UI state, animation, business logic, persistence, and navigation.
  function LotteryCtrl(
    $scope,
    $routeParams,
    $location,
    $timeout,
    $interval,
    LotteryFactory,
    FlightFactory,
    PaxInfoFactory,
    $uibModal,
    UserFactory
  ) {
    // ─── Route params ────────────────────────────────────────────────────────────
    // Parse route parameters for flight and lottery
    var flightId = parseInt($routeParams.flightId, 10);
    var lotteryId = parseInt($routeParams.lotteryId, 10);

    if (isNaN(flightId) || isNaN(lotteryId)) {
      $location.path("/");
      return;
    }

    // Get lottery and flight objects from factories
    var lottery = LotteryFactory.getLotteryById(lotteryId);
    var flight = FlightFactory.getFlightFromTotalFlights(flightId);

    if (!lottery || !flight) {
      $location.path("/flightinfo/" + flightId);
      return;
    }

    // Unique key for draft/submission storage
    var compositeKey = lotteryId + "_" + flightId;

    // ─── Scope state ─────────────────────────────────────────────────────────────
    // Scope state variables for UI and logic
    $scope.lottery = lottery; // Current lottery config
    $scope.flight = flight; // Current flight info
    $scope.isRunning = false; // Animation running flag
    $scope.results = null; // Results of the lottery (per gift)
    $scope.hasPaxList = false; // True if passenger list is loaded
    $scope.draftSaved = false; // True if draft was just saved
    $scope.isSubmitted = false; // True if results have been submitted
    $scope.showPreRunStep = false; // True when the user has entered the pre-draw confirmation step
    $scope.winnerReveal = { show: false, seat: "", name: "" }; // Overlay for winner animation

    // Helper: Returns true if any giftResult has more than one winner
    $scope.hasMultipleWinners = function(results) {
      if (!results || !results.length) return false;
      for (var i = 0; i < results.length; i++) {
        if (results[i].winners && results[i].winners.length > 1) {
          return true;
        }
      }
      return false;
    };
    $scope.toggleResults = false; // Collapse/expand results panel
    $scope.hasEligiblePassengers = false; // True if there are eligible passengers for the lottery
    $scope.eligiblePassengersMessage = ""; // Message to show if no eligible passengers

    // ─── Slot-machine animation state ───────────────────────────────────────────
    // Animation state for slot-machine effect
    $scope.animSeat = ""; // Current seat displayed
    $scope.animSeatChars = []; // Digits/letters of seat
    $scope.animFlip = false; // Flip animation toggle
    $scope.animProgress = 0; // Progress bar value
    $scope.animParticles = []; // Array of seat-number animation particles

    var _fastInterval = null;
    var _animStop = false;
    var _progInterval = null;

    // ─── Passengers ──────────────────────────────────────────────────────────────
    var allPassengers = [];
    var eligiblePool = [];
    var lotteryDemoTargetPassengers = 180;
    var lotteryDemoEnabled = false;

    function setAnimSeat(seat) {
      $scope.animFlip = !$scope.animFlip;
      $scope.animSeat = seat || "";
      $scope.animSeatChars = (seat || "").split("");
    }

    function getPaxKey(pax) {
      if (!pax) return "";
      return (pax.PNR || "") + "_" + (pax.SeatNo || "");
    }

    function isSamePax(a, b) {
      return getPaxKey(a) === getPaxKey(b);
    }

    function isValidEmail(email) {
      if (!email) return false;
      var value = email.trim().toLowerCase();
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    }

    function isValidPhone(phone) {
      if (!phone) return false;
      var value = phone.trim();
      return /^[0-9+\-\s()]{6,20}$/.test(value);
    }

    function buildAnimationParticles(source) {
      // animParticles: Array of seat-number animation particles for the slot-machine effect.
      // Each particle is a seat label with randomized style and animation, used for the animated overlay.
      var shades = ["#9ca3af", "#6b7280", "#374151", "#9ca3af", "#4b5563"];
      var particles = [];
      var maxParticles = 30;

      for (var k = 0; k < maxParticles; k++) {
        var seat = "??";

        if (source && source.length) {
          var p = source[Math.floor(Math.random() * source.length)];
          seat = (p.SeatNo || "??").toString();
        }

        var rot = (Math.random() * 30 - 15).toFixed(1) + "deg";

        particles.push({
          seat: seat, // The seat number to display
          style: {
            left: (Math.random() * 96).toFixed(1) + "%", // Horizontal position
            color: shades[Math.floor(Math.random() * shades.length)], // Text color
            "animation-duration": (4 + Math.random() * 5).toFixed(1) + "s",
            "animation-delay": "-" + (Math.random() * 7).toFixed(1) + "s",
            "--lottoRot": rot, // Rotation for visual effect
          },
        });
      }

      return particles;
    }

    function getRandomEligibleSeat() {
      if (eligiblePool && eligiblePool.length) {
        var p = eligiblePool[Math.floor(Math.random() * eligiblePool.length)];
        return (p.SeatNo || "??").toString();
      }

      var d = "0123456789";
      var cols = "ABCDEF";

      return (
        d[Math.floor(Math.random() * 10)] +
        d[Math.floor(Math.random() * 10)] +
        d[Math.floor(Math.random() * 10)] +
        cols[Math.floor(Math.random() * 6)]
      );
    }

    function slowCycleEligible(speed) {
      if (_animStop) return;

      setAnimSeat(getRandomEligibleSeat());

      $timeout(function () {
        slowCycleEligible(Math.min(speed + 42, 620));
      }, speed);
    }

    function padSeatNumber(row) {
      var value = String(row);
      while (value.length < 3) {
        value = "0" + value;
      }
      return value;
    }

    function createDemoPassenger(row, seatLetter, cabin, index) {
      var seatNo = padSeatNumber(row) + seatLetter;
      var code = padSeatNumber(index + 1);

      return {
        SeatNo: seatNo,
        Fullname: "Demo Passenger " + code,
        GivenName: "Demo",
        Surname: "Passenger " + code,
        PNR: "DM" + code,
        UCI: 900000 + index,
        Cabin: cabin,
        SeatMode: "Seat",
        IsEmptySeat: false,
      };
    }

    function buildDemoPassengers(existingPassengers, targetCount) {
      var passengers = Array.isArray(existingPassengers) ? existingPassengers.slice() : [];
      var seenSeats = {};
      var letters = ["A", "B", "C", "D", "E", "F"];
      var row = 1;
      var index = 0;

      passengers.forEach(function (pax) {
        if (pax && pax.SeatNo) {
          seenSeats[String(pax.SeatNo)] = true;
        }
      });

      while (passengers.length < targetCount) {
        for (var i = 0; i < letters.length && passengers.length < targetCount; i++) {
          var seatNo = padSeatNumber(row) + letters[i];

          if (seenSeats[seatNo]) {
            continue;
          }

          passengers.push(
            createDemoPassenger(row, letters[i], row <= 4 ? "C" : "Y", index)
          );
          seenSeats[seatNo] = true;
          index++;
        }

        row++;
      }

      return passengers;
    }

    $scope.$on("$destroy", function () {
      _animStop = true;

      if (_fastInterval) {
        $interval.cancel(_fastInterval);
        _fastInterval = null;
      }

      if (_progInterval) {
        $interval.cancel(_progInterval);
        _progInterval = null;
      }
    });

    // Load passenger list and restore draft/submission state on controller init
    PaxInfoFactory.getPaxInfoList(flightId, function (passengerList) {
      allPassengers = [];
      eligiblePool = [];

      if (Array.isArray(passengerList)) {
        allPassengers = passengerList;
        $scope.hasPaxList = allPassengers.length > 0;
      } else {
        allPassengers = [];
        $scope.hasPaxList = false;
      }

      if (lotteryDemoEnabled && allPassengers.length < lotteryDemoTargetPassengers) {
        allPassengers = buildDemoPassengers(allPassengers, lotteryDemoTargetPassengers);
        $scope.hasPaxList = allPassengers.length > 0;
      }

      updateEligibilityState();

      var submitted = LotteryFactory.getSubmittedLotteriesFromStorage() || [];
      $scope.isSubmitted = submitted.some(function (x) {
        return x.Id === compositeKey;
      }) || LotteryFactory.hasPendingResult(compositeKey);

      // Restore draft results only when passengers are available or results were
      // already submitted. A draft without passengers is stale and misleading.
      var draft = LotteryFactory.getDraftResult(lotteryId, flightId);
      if (draft && draft.GiftResults && ($scope.hasPaxList || $scope.isSubmitted)) {
        $scope.results = draft.GiftResults;
      }

      $scope.$applyAsync();
    });

    // ─── Navigation ──────────────────────────────────────────────────────────────
    // Navigate back to flight info page
    $scope.back = function () {
      $location.path("/flightinfo/" + flightId);
    };

    // Navigate to the main lottery page (reset animation state)
    $scope.goToLottery = function () {
      _animStop = true;

      if (_fastInterval) {
        $interval.cancel(_fastInterval);
        _fastInterval = null;
      }

      if (_progInterval) {
        $interval.cancel(_progInterval);
        _progInterval = null;
      }

      $scope.isRunning = false;
      $scope.showPreRunStep = false;
      $location.path("/lottery/" + flightId + "/" + lotteryId);
    };

    // ─── Run lottery ─────────────────────────────────────────────────────────────
    // First entry point: shows the dedicated pre-draw step with the large CTA button
    $scope.runLottery = function () {
      if (
        $scope.isRunning ||
        !$scope.hasPaxList ||
        !$scope.hasEligiblePassengers ||
        $scope.results ||
        $scope.isSubmitted
      ) {
        return;
      }

      $scope.showPreRunStep = true;
    };

    $scope.cancelPreRunStep = function () {
      if ($scope.isRunning) return;
      $scope.showPreRunStep = false;
    };

    // Main entry point: runs the lottery animation and computes results
    $scope.beginLotteryDraw = function () {
      if (
        $scope.isRunning ||
        !$scope.hasPaxList ||
        !$scope.hasEligiblePassengers ||
        $scope.results ||
        $scope.isSubmitted
      ) {
        return;
      }

      eligiblePool = buildEligiblePool(allPassengers, lottery.PassengersType);

      $scope.animParticles = buildAnimationParticles(
        eligiblePool && eligiblePool.length ? eligiblePool : allPassengers
      );

      $scope.showPreRunStep = false;
      $scope.isRunning = true;
      $scope.results = null;
      $scope.animProgress = 0;
      _animStop = false;

      setAnimSeat(getRandomEligibleSeat());

      var _startTime = Date.now();
      var _totalMs = 5200;

      if (_progInterval) {
        $interval.cancel(_progInterval);
      }

      _progInterval = $interval(function () {
        $scope.animProgress = Math.min(100, ((Date.now() - _startTime) / _totalMs) * 100);
      }, 50);

      if (_fastInterval) {
        $interval.cancel(_fastInterval);
      }

      _fastInterval = $interval(function () {
        setAnimSeat(getRandomEligibleSeat());
      }, 48);

      $timeout(function () {
        if (_fastInterval) {
          $interval.cancel(_fastInterval);
          _fastInterval = null;
        }

        slowCycleEligible(70);
      }, 2600);

      $timeout(function () {
        _animStop = true;

        if (_progInterval) {
          $interval.cancel(_progInterval);
          _progInterval = null;
        }

        $scope.animProgress = 100;

        var finalResults = executeLottery(allPassengers, lottery);
        $scope.results = finalResults;

        var finalSeat = null;

        if (
          finalResults &&
          finalResults.length &&
          finalResults[0].winners &&
          finalResults[0].winners.length
        ) {
          finalSeat = (finalResults[0].winners[0].SeatNo || "").toString();
        }

        if (!finalSeat) {
          finalSeat = getRandomEligibleSeat();
        }

        $timeout(function () {
          setAnimSeat(finalSeat);
          $scope.animFlip = !$scope.animFlip;
        }, 180);

        $timeout(function () {
          var winnerName = "";
          var winnerSeat = finalSeat;

          if (
            finalResults &&
            finalResults.length &&
            finalResults[0].winners &&
            finalResults[0].winners.length
          ) {
            winnerName = finalResults[0].winners[0].Fullname || "";
            winnerSeat = finalResults[0].winners[0].SeatNo || finalSeat;
          }

          $scope.winnerReveal = {
            show: true,
            seat: winnerSeat,
            name: winnerName,
            gift: finalResults && finalResults.length ? finalResults[0].gift : null,
            winners: finalResults && finalResults.length ? finalResults[0].winners : [],
          };

          autoSaveDraft();
        }, 820);
      }, 5200);
    };

    // ─── Draw algorithm ──────────────────────────────────────────────────────────
    // Core draw logic: executes the lottery for all gifts, picking winners/substitutes
    function executeLottery(passengers, lotteryConfig) {
      var pool = buildEligiblePool(passengers, lotteryConfig.PassengersType);
      var shuffled = fisherYatesShuffle(pool.slice());
      var used = [];
      var giftResults = [];

      angular.forEach(lotteryConfig.Gifts || [], function (gift) {
        var winners = drawPassengers(shuffled, used, gift.NumberOfWinners);
        var substitutes = drawPassengers(shuffled, used, gift.NumberOfSubstitutes);

        var selected = winners.map(function (p) {
          return {
            pax: p,
            email: "",
            phone: "",
          };
        });

        giftResults.push({
          gift: gift,
          winners: winners,
          substitutes: substitutes,
          selected: selected,
        });
      });

      return giftResults;
    }

    // Filters passengers by lottery config (Business/Economy/All)
    function buildEligiblePool(passengers, passengersType) {
      var t = passengersType;

      if (t === 1 || t === "Business" || t === "C") {
        return passengers.filter(function (p) {
          return p.Cabin === "C";
        });
      }

      if (t === 2 || t === "Economy" || t === "Y") {
        return passengers.filter(function (p) {
          return p.Cabin === "Y";
        });
      }

      return passengers.slice();
    }

    // Unbiased random shuffle (Fisher-Yates)
    function fisherYatesShuffle(arr) {
      for (var i = arr.length - 1; i > 0; i--) {
        var j = Math.floor(Math.random() * (i + 1));
        var tmp = arr[i];
        arr[i] = arr[j];
        arr[j] = tmp;
      }

      return arr;
    }

    // Picks up to 'count' unique passengers from the shuffled pool, skipping already used
    function drawPassengers(shuffled, used, count) {
      var picked = [];

      for (var i = 0; i < shuffled.length && picked.length < count; i++) {
        var uid = getPaxKey(shuffled[i]);

        if (used.indexOf(uid) === -1) {
          picked.push(shuffled[i]);
          used.push(uid);
        }
      }

      return picked;
    }

    // ─── Recipient selection ─────────────────────────────────────────────────────
    // Returns true if the given passenger is currently selected as a final recipient for this gift
    $scope.isSelected = function (giftResult, pax) {
      return giftResult.selected.some(function (s) {
        return isSamePax(s.pax, pax);
      });
    };

    // Returns the contact object for a selected passenger (for editing email/phone)
    $scope.getContact = function (giftResult, pax) {
      for (var i = 0; i < giftResult.selected.length; i++) {
        if (isSamePax(giftResult.selected[i].pax, pax)) {
          return giftResult.selected[i];
        }
      }

      return null;
    };

    // Toggles selection of a passenger as a final recipient for a gift
    // Enforces max number of winners
    $scope.toggleRecipient = function (giftResult, pax) {
      var idx = -1;

      for (var i = 0; i < giftResult.selected.length; i++) {
        if (isSamePax(giftResult.selected[i].pax, pax)) {
          idx = i;
          break;
        }
      }

      if (idx > -1) {
        giftResult.selected.splice(idx, 1);
      } else {
        if (giftResult.selected.length >= giftResult.gift.NumberOfWinners) {
          return;
        }

        giftResult.selected.push({
          pax: pax,
          email: "",
          phone: "",
        });
      }

      autoSaveDraft();
    };

    // Returns true if the passenger can be selected (not exceeding max winners)
    $scope.canSelectPassenger = function (giftResult, pax) {
      return (
        $scope.isSelected(giftResult, pax) ||
        giftResult.selected.length < giftResult.gift.NumberOfWinners
      );
    };

    var _contactSavePromise = null;

    // Debounced auto-save for contact details (email/phone)
    $scope.autoSaveContactDetails = function () {
      if (_contactSavePromise) {
        $timeout.cancel(_contactSavePromise);
      }

      _contactSavePromise = $timeout(function () {
        autoSaveDraft();
        _contactSavePromise = null;
      }, 400);
    };

    // Returns true if email is valid or empty (for UI validation feedback)
    $scope.isValidEmailForView = function (email) {
      return !email || isValidEmail(email);
    };

    // Returns true if phone is valid or empty (for UI validation feedback)
    $scope.isValidPhoneForView = function (phone) {
      return !phone || isValidPhone(phone);
    };

    // Returns true if the selected count has reached the configured number of winners
    $scope.isGiftSelectionComplete = function (giftResult) {
      return giftResult.selected.length >= giftResult.gift.NumberOfWinners;
    };

    // ─── Submit gate ─────────────────────────────────────────────────────────────
    // Returns true if the number of selected recipients does not exceed the allowed winners
    $scope.isGiftSelectionValid = function (giftResult) {
      return giftResult.selected.length <= giftResult.gift.NumberOfWinners;
    };

    // Returns true if both email and phone are valid for a contact
    $scope.isContactValid = function (contact) {
      return !!contact && isValidEmail(contact.email) && isValidPhone(contact.phone);
    };

    // Returns true if all selected recipients for a gift have valid contact details
    $scope.isGiftReadyToSubmit = function (giftResult) {
      if (!$scope.isGiftSelectionValid(giftResult)) return false;

      return giftResult.selected.every(function (s) {
        return $scope.isContactValid(s);
      });
    };

    // Returns true if all gifts are ready to submit (validation passes)
    $scope.isSubmitEnabled = function () {
      if (!$scope.results || !$scope.results.length) return false;

      // At least one selected recipient across all gifts
      var totalSelected = $scope.results.reduce(function (sum, gr) {
        return sum + gr.selected.length;
      }, 0);
      if (totalSelected === 0) return false;

      return $scope.results.every(function (gr) {
        return $scope.isGiftReadyToSubmit(gr);
      });
    };

    // ─── Persistence ─────────────────────────────────────────────────────────────
    // Saves the current results as a draft in local storage
    function autoSaveDraft() {
      if (!$scope.results) return;

      LotteryFactory.saveDraftResult({
        id: compositeKey,
        lotteryId: lotteryId,
        flightId: flightId,
        giftResults: $scope.results,
        savedAt: new Date().toISOString(),
      });
    }

    // Manual save draft action (shows feedback)
    $scope.saveDraft = function () {
      autoSaveDraft();
      $scope.draftSaved = true;

      $timeout(function () {
        $scope.draftSaved = false;
      }, 2500);
    };

    // Submits the final results (marks as submitted, removes draft)
    $scope.submitResults = function () {
      if (!$scope.isSubmitEnabled()) return;
      var user = UserFactory.getUser();
      var crewMemberId = user ? (user.aimsId || user.AimsId || null) : null;

      var submission = {
        id: compositeKey,
        lotteryId: lotteryId,
        flightId: flightId,
        crewMemberId: crewMemberId,
        flightDisplay: flight.FlightNo + ' ' + (flight.Departure || '') + '-' + (flight.Arrival || '') + (flight.ScheduledDeparture ? ' ' + new Date(flight.ScheduledDeparture).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : ''),
        giftResults: $scope.results,
        submittedAt: new Date().toISOString(),
      };

      LotteryFactory.submitResult(submission);
      LotteryFactory.removeDraftResult(lotteryId, flightId);
      $scope.isSubmitted = true;
      $scope.winnerReveal = { show: false, seat: "", name: "" };
    };

    // Opens a modal warning before rerunning the lottery (if not submitted)
    $scope.openRerunWarning = function () {
      if ($scope.isSubmitted) return;

      var modalInstance = $uibModal.open({
        templateUrl: "views/Dialogs/dlgRerunLotteryWarning.html",
        controller: "RerunLotteryWarningModalCtrl",
        size: "md",
        backdrop: "static",
        keyboard: false,
      });

      modalInstance.result.then(function (confirmed) {
        if (confirmed) {
          $scope.confirmRerunLottery();
        }
      });
    };

    // Confirms rerun: clears all state, removes draft, and starts a fresh lottery run
    $scope.confirmRerunLottery = function () {
      if ($scope.isSubmitted) return;
      if ($scope.isRunning) return;
      if (!$scope.hasPaxList) return;
      if (!$scope.hasEligiblePassengers) return;

      // Stop any running animation just in case
      _animStop = true;

      if (_fastInterval) {
        $interval.cancel(_fastInterval);
        _fastInterval = null;
      }

      if (_progInterval) {
        $interval.cancel(_progInterval);
        _progInterval = null;
      }

      // Clear current in-memory state
      $scope.results = null;
      $scope.draftSaved = false;
      $scope.showPreRunStep = false;
      $scope.animSeat = "";
      $scope.animSeatChars = [];
      $scope.animProgress = 0;
      $scope.winnerReveal = { show: false, seat: "", name: "" };
      $scope.animParticles = [];

      // Remove local draft so only latest run survives
      LotteryFactory.removeDraftResult(lotteryId, flightId);

      // Start fresh run with same lottery configuration
      $scope.runLottery();
    };

    function updateEligibilityState() {
      eligiblePool = buildEligiblePool(allPassengers, lottery.PassengersType);

      $scope.hasEligiblePassengers = eligiblePool.length > 0;
      $scope.eligiblePassengersMessage = "";

      if (!$scope.hasPaxList) {
        return;
      }

      if ($scope.hasEligiblePassengers) {
        return;
      }

      var passengerType = lottery.PassengersType;

      if (passengerType === 1 || passengerType === "Business" || passengerType === "C") {
        $scope.eligiblePassengersMessage =
          "No eligible Business Class passengers are available for this lottery.";
        return;
      }

      if (passengerType === 2 || passengerType === "Economy" || passengerType === "Y") {
        $scope.eligiblePassengersMessage =
          "No eligible Economy Class passengers are available for this lottery.";
        return;
      }

      $scope.eligiblePassengersMessage = "No eligible passengers are available for this lottery.";
    }
  }
})();
